import {
  PRICES,
  roleLabel,
  TEAM_MIN_EDITORS,
  type InviteCreated,
  type Role,
  type WorkspaceSummary,
} from '@nolens/domain';
import { formatDate } from '@nolens/i18n';
import {
  Avatar,
  Banner,
  Button,
  Dialog,
  Field,
  Input,
  Select,
  useLocale,
  useToast,
} from '@nolens/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { QueryView } from '../components/Query';
import { ApiError } from '../lib/api';
import { useInvites, useMembers, useWorkspaceActions, useWorkspaceBilling } from '../lib/queries';
import { eur } from '../i18n/landing';
import { spaceCrumbs, WithMe } from './workspace';
import { shellText } from '../i18n/shell';
import { membersText } from '../i18n/members';

const ROLES: Role[] = ['owner', 'editor', 'viewer'];

export function MembersPage() {
  const { workspaceId } = useParams();
  const locale = useLocale();
  const t = membersText(locale);
  const st = shellText(locale);
  return (
    <WithMe
      current={workspaceId}
      crumbs={(me) => spaceCrumbs(me, workspaceId, locale, { label: st.members })}
    >
      {(me) => {
        const ws = me.workspaces.find((w) => w.id === workspaceId);
        if (!ws || ws.kind !== 'team') return <p className="text-text-2">{t.onlyTeams}</p>;
        return <Members ws={ws} />;
      }}
    </WithMe>
  );
}

function Members({ ws }: { ws: WorkspaceSummary }) {
  const { id: workspaceId, name } = ws;
  const owner = ws.role === 'owner';
  const locale = useLocale();
  const t = membersText(locale);
  const members = useMembers(workspaceId);
  const invites = useInvites(workspaceId, owner);
  const act = useWorkspaceActions(workspaceId);
  const toast = useToast();
  const [created, setCreated] = useState<InviteCreated | null>(null);
  // Equipa paga: um editor acima do mínimo faturado acrescenta um lugar (pede confirmação)
  const billing = useWorkspaceBilling(owner ? workspaceId : undefined);
  const paid = billing.data?.subscribed === true;
  const editors = members.data?.filter((m) => m.role !== 'viewer').length ?? 0;
  const addsSeat = paid && editors >= TEAM_MIN_EDITORS;
  const [confirmSeat, setConfirmSeat] = useState<{ userId?: string } | null>(null);
  // Email opcional: com ele, a API envia a ligação; sem ele (ou se o envio falhar), copia-se
  const [inviteTo, setInviteTo] = useState('');
  const invite = (role: 'editor' | 'viewer') =>
    act.invite.mutate(
      { role, email: inviteTo.trim() || undefined },
      {
        onSuccess: (c) => {
          setCreated(c);
          if (c.emailed && c.email) {
            toast(t.inviteSent(c.email), 'positive');
            setInviteTo('');
          }
        },
      },
    );
  const inviteEditor = () => invite('editor');
  const addSeat = () => {
    if (confirmSeat?.userId) act.setRole.mutate({ userId: confirmSeat.userId, role: 'editor' });
    else inviteEditor();
    setConfirmSeat(null);
  };
  const error = [act.setRole, act.removeMember, act.invite, act.revoke]
    .map((m) => m.error)
    .find((e) => e instanceof ApiError) as ApiError | undefined;
  const inviteUrl = created ? `${window.location.origin}/convite/${created.token}` : '';

  return (
    <section className="max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-tight">{t.title(name)}</h1>
      {error && (
        <Banner tone="critical" className="mt-4">
          {error.code === 'VALIDATION_FAILED' ? t.needsOwner : error.message}
        </Banner>
      )}
      <QueryView q={members}>
        {(list) => (
          <ul className="mt-5 divide-y divide-border rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]">
            {list.map((m) => (
              <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
                <Avatar name={m.email ?? '?'} size={32} />
                <span className="min-w-0 flex-1 truncate">
                  {m.email ?? t.noEmail}
                  {m.you && <span className="text-text-3">{t.you}</span>}
                </span>
                {owner ? (
                  <div className="w-32 shrink-0">
                    <Select
                      aria-label={t.roleOf(m.email ?? t.member)}
                      value={m.role}
                      onChange={(e) => {
                        const role = e.target.value as Role;
                        if (role === 'editor' && m.role === 'viewer' && addsSeat)
                          setConfirmSeat({ userId: m.userId });
                        else act.setRole.mutate({ userId: m.userId, role });
                      }}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r, locale)}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <span className="text-sm text-text-2">{roleLabel(m.role, locale)}</span>
                )}
                {owner && !m.you && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => act.removeMember.mutate(m.userId)}
                  >
                    {t.remove}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </QueryView>

      {owner && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold">{t.invite}</h2>
          <p className="mt-1 text-sm text-text-2">{t.inviteHint}</p>
          {paid && (
            <p className="mt-1 text-sm text-text-2" data-seats-note>
              {t.seatsNote(eur(locale, PRICES.team.monthly), eur(locale, PRICES.team.yearly))}
            </p>
          )}
          <div className="mt-3 max-w-sm">
            <Field label={t.inviteEmail} hint={t.inviteEmailHint}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={inviteTo}
                  onChange={(e) => setInviteTo(e.target.value)}
                  placeholder={t.inviteEmailPlaceholder}
                  autoComplete="off"
                  data-invite-email
                />
              )}
            </Field>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              loading={act.invite.isPending}
              onClick={() => (addsSeat ? setConfirmSeat({}) : inviteEditor())}
            >
              {t.inviteEditor}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => invite('viewer')}>
              {t.inviteViewer}
            </Button>
          </div>
          {created?.email && !created.emailed && (
            <p className="mt-4 text-sm text-text-2" data-invite-not-sent>
              {t.inviteNotSent(created.email)}
            </p>
          )}
          {created && (
            <div className="mt-4 flex gap-2">
              <input
                readOnly
                aria-label={t.inviteLink}
                value={inviteUrl}
                className="h-10 min-w-0 flex-1 rounded-[10px] bg-surface-2 px-3 text-[14px]"
                onFocus={(e) => e.target.select()}
              />
              <Button
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(inviteUrl);
                  toast(t.inviteCopied, 'positive');
                }}
              >
                {t.copy}
              </Button>
            </div>
          )}
          <QueryView q={invites}>
            {(list) =>
              list.length ? (
                <ul className="mt-4 flex flex-col gap-1 text-sm text-text-2">
                  {list.map((i) => (
                    <li key={i.id} className="flex items-center gap-2">
                      {t.inviteFor(roleLabel(i.role, locale), formatDate(locale, i.expiresAt, {}))}
                      {i.email && <span className="text-text-3"> · {i.email}</span>}
                      <button
                        type="button"
                        className="text-critical hover:underline"
                        onClick={() => act.revoke.mutate(i.id)}
                      >
                        {t.revoke}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null
            }
          </QueryView>
        </div>
      )}
      <Dialog
        open={confirmSeat !== null}
        onClose={() => setConfirmSeat(null)}
        title={t.seatTitle}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmSeat(null)}>
              {t.cancel}
            </Button>
            <Button onClick={addSeat}>{t.seatContinue}</Button>
          </div>
        }
      >
        <p className="text-[14px] text-text-2">
          {t.seatHint(editors, eur(locale, PRICES.team.monthly))}
        </p>
      </Dialog>
    </section>
  );
}
